import { useEffect, useMemo, useState } from 'react'
import { Check, Copy, Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useBuildStorageLayout,
  useCapacitySummary,
  useLayoutTemplate,
  useStorageRoots,
} from '@/hooks/useStorage'
import { kindWord, levelsFromTemplate, planBlueprint } from '@/lib/storageWizard'
import { CopyLayoutOptions } from './CopyLayoutOptions'

/**
 * Copy THIS unit's layout onto an empty one.
 *
 * The same act as the setup wizard's "copy from" panel, pointed the other
 * way: somebody looking at the freezer they want to clone should not have to
 * go and find the new unit first, then remember which one they meant.
 *
 * Only units with no layout are offered as a target. `buildLayout` refuses to
 * build over an existing tree — a layout describes a whole unit, so merging
 * two descriptions would interleave them — and offering a unit the server
 * will refuse is how a dialog teaches people to distrust its list.
 */
export function CopyLayoutDialog({
  sourceId,
  sourceLabel,
  onClose,
  onCopied,
}: {
  /** The unit being copied FROM. Null closes the dialog. */
  sourceId: string | null
  sourceLabel: string
  onClose: () => void
  /** Called with the target's id once the layout exists. */
  onCopied?: (targetId: string) => void
}) {
  const roots = useStorageRoots()
  const summary = useCapacitySummary()
  const template = useLayoutTemplate(sourceId)
  const build = useBuildStorageLayout()

  const [targetId, setTargetId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!sourceId) return
    setTargetId(null)
    setError(null)
  }, [sourceId])

  // Empty units only — `declared === 0` is the same signal the table prints as
  // "Not set up", and building over an existing tree is refused server-side.
  const targets = useMemo(() => {
    const empty = new Set(
      (summary.data ?? []).filter((r) => r.declared === 0).map((r) => r.nodeId),
    )
    return (roots.data ?? []).filter((r) => r.id !== sourceId && empty.has(r.id))
  }, [roots.data, summary.data, sourceId])

  const levels = useMemo(
    () => (template.data ? levelsFromTemplate(template.data.levels) : []),
    [template.data],
  )
  const blueprint = useMemo(() => planBlueprint(levels), [levels])
  const deepest = blueprint[blueprint.length - 1]

  const copy = () => {
    if (!targetId) return
    setError(null)
    build.mutate(
      // The layout only: nothing else about the source goes to the target.
      { rootId: targetId, levels },
      {
        onSuccess: () => {
          onCopied?.(targetId)
          onClose()
        },
        onError: (e) => setError(e.message),
      },
    )
  }

  return (
    <Dialog open={sourceId !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='flex max-h-[85vh] flex-col gap-0 p-0 sm:max-w-lg'>
        <DialogHeader className='border-b px-6 pb-4 pt-5'>
          <DialogTitle className='flex items-center gap-2'>
            <Copy className='h-5 w-5 text-primary' />
            Copy {sourceLabel}'s layout
          </DialogTitle>
          <DialogDescription>
            Builds the same structure in a unit that has none yet. {sourceLabel} is not changed.
          </DialogDescription>
        </DialogHeader>

        <div className='min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4'>
          {template.isLoading && (
            <p className='flex items-center gap-2 text-sm text-muted-foreground'>
              <Loader2 className='h-4 w-4 animate-spin' />
              Reading {sourceLabel}'s layout…
            </p>
          )}

          {template.error && (
            <p className='text-sm text-destructive'>
              Could not read that layout: {template.error.message}
            </p>
          )}

          {template.data && levels.length === 0 && (
            <p className='text-sm text-muted-foreground'>
              {sourceLabel} has no layout to copy yet.
            </p>
          )}

          {template.data && levels.length > 0 && (
            <>
              <div className='space-y-1.5'>
                <Label>Copy it to</Label>
                {targets.length === 0 ? (
                  // Two different absences. "Nothing is empty" and "there are
                  // no other units" send a person to different places.
                  <p className='rounded-md border bg-muted/40 p-3 text-[13px] text-muted-foreground'>
                    Every other storage unit already has a layout. Add a storage unit first — a
                    layout can only be built into an empty one.
                  </p>
                ) : (
                  <Select value={targetId ?? ''} onValueChange={setTargetId}>
                    <SelectTrigger>
                      <SelectValue placeholder='Choose an empty unit…' />
                    </SelectTrigger>
                    <SelectContent>
                      {targets.map((r) => (
                        <SelectItem key={r.id} value={r.id}>
                          {r.name ?? r.code}
                          {r.name && ` (${r.code})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className='rounded-md border p-3'>
                <p className='mb-1 text-[13px] font-medium'>What will be built</p>
                <ul className='space-y-0.5 text-[13px] text-muted-foreground'>
                  {blueprint.map((row, i) => (
                    <li key={i}>
                      {row.total.toLocaleString()} {kindWord(row.kind, row.total)}
                      {i > 0 && ` — ${row.perParent} in each ${kindWord(blueprint[i - 1].kind, 1)}`}
                    </li>
                  ))}
                </ul>
                {deepest && (
                  <p className='mt-1.5 text-[13px] font-medium'>
                    {deepest.total.toLocaleString()} {kindWord(deepest.kind, deepest.total)} in
                    total
                  </p>
                )}
              </div>

              <CopyLayoutOptions template={template.data} />
            </>
          )}

          {error && (
            <p className='rounded-md border border-red-200 bg-red-50 p-2 text-[13px] text-red-800'>
              {error} — nothing was created.
            </p>
          )}
        </div>

        <div className='flex items-center justify-end gap-2 border-t px-6 py-3'>
          <Button variant='outline' onClick={onClose} disabled={build.isPending}>
            Cancel
          </Button>
          <Button
            className='gap-2'
            disabled={!targetId || levels.length === 0 || build.isPending}
            onClick={copy}
          >
            {build.isPending ? (
              <Loader2 className='h-4 w-4 animate-spin' />
            ) : (
              <Check className='h-4 w-4' />
            )}
            Build the copy
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
